import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, Loader2, RefreshCw } from "lucide-react";
import api from "../../lib/api";

/**
 * Brief 3.5 PR 3 — Re-review admin page.
 *
 * Sweeps approved/live merchants whose verdict is currently a blocker
 * under the active rules version. Compliance officer reviews case-by-
 * case; no auto-demotion.
 */
export default function AdminReReview() {
  const [state, setState] = useState({ loading: true, err: null, rows: [], rulesVersion: null });

  const load = async () => {
    setState((s) => ({ ...s, loading: true, err: null }));
    try {
      const { data } = await api.get("/admin/compliance/re-review");
      setState({
        loading: false, err: null,
        rows: data?.rows || [], rulesVersion: data?.rules_version,
      });
    } catch (e) {
      setState({
        loading: false,
        err: e?.response?.data?.detail || "Sweep failed.",
        rows: [], rulesVersion: null,
      });
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-re-review-page" className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 mb-1">— Compliance</div>
          <h1 className="text-[22px] tracking-tight text-white font-medium">Re-review recommended</h1>
          <p className="mt-1 text-[13px] text-zinc-400 max-w-2xl">
            Approved or live merchants whose verdict would be a blocker under
            the current rules. Compliance reviews case-by-case — no automatic
            demotion. Dismissals are scoped to the current rules version so
            tightening rules later will re-surface affected merchants.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="admin-re-review-refresh"
          className="inline-flex items-center gap-2 rounded-full border border-white/[0.10] bg-white/[0.02] px-3 py-1.5 text-[12.5px] text-zinc-300 hover:bg-white/[0.05]"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${state.loading ? "animate-spin" : ""}`} /> Re-run sweep
        </button>
      </header>

      {state.rulesVersion && (
        <div className="text-[11.5px] text-zinc-500 font-mono">
          Active rules version: <span className="text-zinc-300">{state.rulesVersion}</span>
        </div>
      )}

      {state.loading && (
        <div className="flex items-center gap-2 text-[13px] text-zinc-400" data-testid="admin-re-review-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Sweeping merchants…
        </div>
      )}

      {state.err && (
        <div
          data-testid="admin-re-review-error"
          className="rounded-lg border border-rose-400/30 bg-rose-500/[0.05] px-4 py-3 text-[13px] text-rose-200"
        >
          {state.err}
        </div>
      )}

      {!state.loading && !state.err && state.rows.length === 0 && (
        <div
          data-testid="admin-re-review-empty"
          className="rounded-2xl border border-emerald-400/30 bg-emerald-500/[0.05] px-5 py-4 text-[13.5px] text-emerald-100"
        >
          No approved or live merchants are flagged as blockers under the current rules. Nothing to review.
        </div>
      )}

      {!state.loading && state.rows.length > 0 && (
        <ul data-testid="admin-re-review-list" className="space-y-2">
          {state.rows.map((r) => (
            <li
              key={r.merchant_id}
              data-testid={`admin-re-review-row-${r.merchant_id}`}
              className="rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-3 flex items-start gap-3"
            >
              <AlertTriangle className="h-4 w-4 mt-1 shrink-0 text-amber-300" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[14px] text-white font-medium">
                    {r.company_name || r.merchant_id}
                  </span>
                  <span className="text-[11.5px] text-zinc-500 font-mono">{r.ref}</span>
                  <span className="text-[11px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-emerald-400/[0.08] border border-emerald-400/30 text-emerald-100">
                    {r.status}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-rose-400/[0.08] border border-rose-400/30 text-rose-100">
                    New verdict: blocker
                  </span>
                </div>
                {r.primary_blocker && (
                  <div className="mt-1 text-[12.5px] text-zinc-300" data-testid={`admin-re-review-row-${r.merchant_id}-reason`}>
                    {r.primary_blocker}
                  </div>
                )}
              </div>
              <Link
                to={`/admin/merchants/${r.merchant_id}`}
                data-testid={`admin-re-review-row-${r.merchant_id}-open`}
                className="inline-flex items-center gap-1 text-[12.5px] text-cyan-200 hover:text-white whitespace-nowrap"
              >
                View merchant <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
